import { apiClient } from './apiClient'
import { withApiErrorHandling } from '../utils/apiErrorWrapper'
import { ResultStatus } from '../types/domain'
import {
  Competition,
  CompetitionTenant,
  Series,
  SeriesFee,
  Participation,
  CompetitionRound,
  Flight,
  CompetitionResult,
  EligibilityResponse,
  CompetitionsQueryParams,
  ReorderFlightParams,
  HoleScoreInput,
  WeeklyCompetitionEligibility,
  BatchParticipationItem,
  UpdateParticipationRequest,
  FlightScoreEntryRequest,
  HoleScoreDTO,
  FlightScoresDTO,
  BatchScoreSubmissionRequest,
  BatchScoreSubmissionResponse,
  SeriesTeeOverridesResponse,
  UpdateSeriesTeeOverridesRequest,
  UpdateSeriesTeeOverridesResponse,
} from '../types/dtos'
import type { VisibilityAudience } from '../types/dtos'
import { ScoringRankingResult } from '../pages/competitions/admin/scoring/scoringTypes'

interface CutResultDTO {
  cutApplied: boolean
  cutLine: number
  madeTheCut: number
  missedTheCut: number
  participationsCut: number[]
}

interface RemoveCutResultDTO {
  cutRemoved: boolean
  participationsRestored: number
}

export interface EBirdieSubmitResponse {
  submitted: number
  skipped: number
  failed: number
  errors: string[]
}

export interface EBirdieCancelResponse {
  cancelled: number
  skipped: number
  failed: number
  errors: string[]
}

export interface RefreshHandicapsResponse {
  participations: Participation[]
  updatedCount: number
  unchangedCount: number
  failedCount: number
  skippedParticipantIdsDueToEbirdieSubmission: number[]
}

export interface WeeklyScoreDetail {
  resultId: number
  playedAt: string
  courseName: string
  teeSetName: string
  totalStrokes: number | null
  stablefordPoints: number
  system36Handicap?: number | null
  isBestScore: boolean | null
  status: ResultStatus
  ebirdieSubmissionStatus: string | null
  ebirdieSubmittedAt: string | null
  ebirdieModificationKey: string | null
}

export interface WeeklyPlayerResult {
  userId: number
  userName: string
  firstName: string
  lastName: string
  clubName?: string | null
  clubAbbreviation?: string | null
  membershipNumber?: string | null
  roundsPlayed: number
  roundsCounted: number
  expectedRounds: number
  totalScore: number
  allScores: number[]
  bestScores: number[]
  participantId?: number
  scoreDetails?: WeeklyScoreDetail[]
}

const competitionErrors = {
  forbidden: 'You do not have permission to access this competition',
  notFound: 'Competition not found'
}

class CompetitionService {
  getCompetitions = withApiErrorHandling(
    async (params: CompetitionsQueryParams = {}): Promise<Competition[]> => {
      const queryParams = new URLSearchParams()
      if (params.from) queryParams.append('from', params.from)
      if (params.to) queryParams.append('to', params.to)
      if (params.tenantId) queryParams.append('tenantId', params.tenantId.toString())
      if (params.categorySlugs && params.categorySlugs.length > 0) {
        queryParams.append('categorySlugs', params.categorySlugs.join(','))
      }
      const url = queryParams.toString() ? `/competitions?${queryParams}` : '/competitions'
      const response = await apiClient.get<Competition[]>(url)
      return response.data
    },
    {
      logContext: 'Failed to fetch competitions',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load competitions' }
    }
  )

  getCompetitionTenants = withApiErrorHandling(
    async (): Promise<CompetitionTenant[]> => {
      const response = await apiClient.get<CompetitionTenant[]>('/competitions/tenants')
      return response.data
    },
    {
      logContext: 'Failed to fetch competition tenants',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load clubs' }
    }
  )

  getCompetitionYears = withApiErrorHandling(
    async (): Promise<number[]> => {
      const response = await apiClient.get<number[]>('/competitions/years')
      return response.data
    },
    {
      logContext: 'Failed to fetch competition years',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load years' }
    }
  )

  getCompetitionById = withApiErrorHandling(
    async (competitionId: number): Promise<Competition> => {
      // Page-load GET: errors render via the caller's error state, not the global toast
      const response = await apiClient.get<Competition>(`/competitions/${competitionId}`, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to fetch competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load competition details' }
    }
  )

  createCompetition = withApiErrorHandling(
    async (competitionData: Record<string, unknown>): Promise<Competition> => {
      const response = await apiClient.post<Competition>('/competitions', competitionData)
      return response.data
    },
    {
      logContext: 'Failed to create competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to create competition' }
    }
  )

  updateCompetition = withApiErrorHandling(
    async (competitionId: number, updateData: Record<string, unknown>): Promise<Competition> => {
      const response = await apiClient.put<Competition>(`/competitions/${competitionId}`, updateData)
      return response.data
    },
    {
      logContext: 'Failed to update competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update competition' }
    }
  )

  deleteCompetition = withApiErrorHandling(
    async (competitionId: number): Promise<{ success: boolean }> => {
      await apiClient.delete(`/competitions/${competitionId}`, { suppressErrorToast: true } as Record<string, unknown>)
      return { success: true }
    },
    {
      logContext: 'Failed to delete competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete competition' }
    }
  )

  copyCompetition = withApiErrorHandling(
    async (competitionId: number, copyData: {
      name: string
      startDate: string
      participationEnabled?: boolean
      isPublic?: boolean
    }): Promise<Competition> => {
      const response = await apiClient.post<Competition>(`/competitions/${competitionId}/copy`, copyData)
      return response.data
    },
    {
      logContext: 'Failed to copy competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to copy competition' }
    }
  )

  // =============== Competition Series Methods ===============

  searchCompetitions = withApiErrorHandling(
    async (query?: string, year?: number): Promise<Competition[]> => {
      const params: CompetitionsQueryParams = {}
      if (year) {
        params.from = `${year}-01-01`
        params.to = `${year}-12-31`
      }
      const competitions = await this.getCompetitions(params)
      if (query && query.trim()) {
        const lowerQuery = query.trim().toLowerCase()
        return competitions.filter(comp =>
          comp.name.toLowerCase().includes(lowerQuery)
        )
      }
      return competitions
    },
    {
      logContext: 'Failed to search competitions',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to search competitions' }
    }
  )

  getCompetitionSeries = withApiErrorHandling(
    async (competitionId: number): Promise<Series[]> => {
      const response = await apiClient.get<Series[]>(`/competitions/${competitionId}/series`, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to fetch series for competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load series' }
    }
  )

  createSeries = withApiErrorHandling(
    async (competitionId: number, seriesData: Record<string, unknown>): Promise<Series> => {
      const response = await apiClient.post<Series>(`/competitions/${competitionId}/series`, seriesData, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to create series for competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to create series' }
    }
  )

  updateSeries = withApiErrorHandling(
    async (competitionId: number, seriesId: number, updateData: Record<string, unknown>): Promise<Series> => {
      const response = await apiClient.put<Series>(`/competitions/${competitionId}/series/${seriesId}`, updateData, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to update series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update series' }
    }
  )

  deleteSeries = withApiErrorHandling(
    async (competitionId: number, seriesId: number): Promise<{ success: boolean }> => {
      await apiClient.delete(`/competitions/${competitionId}/series/${seriesId}`, { suppressErrorToast: true } as Record<string, unknown>)
      return { success: true }
    },
    {
      logContext: 'Failed to delete series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete series' }
    }
  )

  // =============== Series Fee Methods ===============

  getSeriesFees = withApiErrorHandling(
    async (competitionId: number, seriesId: number): Promise<SeriesFee[]> => {
      const response = await apiClient.get<SeriesFee[]>(
        `/competitions/${competitionId}/series/${seriesId}/fees`
      )
      return response.data
    },
    {
      logContext: 'Failed to fetch fees for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load fees' }
    }
  )

  // =============== Registration / Participation Methods ===============

  checkEligibility = withApiErrorHandling(
    async (competitionId: number): Promise<EligibilityResponse> => {
      const response = await apiClient.get<EligibilityResponse>(`/competitions/${competitionId}/eligibility`, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to check eligibility for competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to check eligibility' }
    }
  )

  registerParticipation = withApiErrorHandling(
    async (
      competitionId: number,
      seriesId: number,
      participationData: Record<string, unknown>,
      forceConfirmed?: boolean
    ): Promise<Participation> => {
      const response = await apiClient.post<Participation>(
        `/competitions/${competitionId}/series/${seriesId}/register`,
        { ...participationData, forceConfirmed }
      )
      return response.data
    },
    {
      logContext: 'Failed to register for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to register' }
    }
  )

  registerMe = withApiErrorHandling(
    async (competitionId: number, seriesId: number, notes?: string, startTimePreference?: string, acceptWaitlist?: boolean, cartWish?: boolean): Promise<Participation> => {
      const response = await apiClient.post<Participation>(
        `/competitions/${competitionId}/series/${seriesId}/register-me`,
        { notes, startTimePreference: startTimePreference || 'NONE', acceptWaitlist: acceptWaitlist ?? false, cartWish: cartWish ?? false },
        { suppressErrorToast: true } as Record<string, unknown>
      )
      return response.data
    },
    {
      logContext: 'Failed to self-register for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to register' }
    }
  )

  registerTeam = withApiErrorHandling(
    async (competitionId: number, seriesId: number, data: {
      teamName?: string
      notes?: string
      startTimePreference?: string
      acceptWaitlist?: boolean
      cartWish?: boolean
      teammates: Array<{
        externalPlayerId: string
        firstName: string
        lastName: string
        gender: string
        birthYear?: number | null
        handicapIndex: number
        clubName?: string | null
        clubAbbreviation?: string | null
        membershipNumber?: string | null
        matchedUserId?: number | null
        cartWish?: boolean
      }>
    }): Promise<Participation> => {
      const response = await apiClient.post<Participation>(
        `/competitions/${competitionId}/series/${seriesId}/register-team`,
        {
          teamName: data.teamName,
          notes: data.notes,
          startTimePreference: data.startTimePreference || 'NONE',
          acceptWaitlist: data.acceptWaitlist ?? false,
          cartWish: data.cartWish ?? false,
          teammates: data.teammates
        },
        { suppressErrorToast: true } as Record<string, unknown>
      )
      return response.data
    },
    {
      logContext: 'Failed to register team',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to register team' }
    }
  )

  getMyParticipations = withApiErrorHandling(
    async (): Promise<Participation[]> => {
      const response = await apiClient.get<Participation[]>('/participations/my')
      return response.data
    },
    {
      logContext: 'Failed to fetch my participations',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load registrations' }
    }
  )

  withdrawParticipation = withApiErrorHandling(
    async (participationId: number, sendEmail?: boolean): Promise<Participation> => {
      const query = sendEmail !== undefined ? `?sendEmail=${sendEmail}` : ''
      const response = await apiClient.delete<Participation>(`/participations/${participationId}/withdraw${query}`)
      return response.data
    },
    {
      logContext: 'Failed to withdraw participation',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to withdraw registration' }
    }
  )

  removeParticipantFromTeam = withApiErrorHandling(
    async (participationId: number, participantId: number, sendEmail?: boolean): Promise<Participation> => {
      const query = sendEmail !== undefined ? `?sendEmail=${sendEmail}` : ''
      const response = await apiClient.delete<Participation>(`/participations/${participationId}/participants/${participantId}${query}`)
      return response.data
    },
    {
      logContext: 'Failed to remove participant from team',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to remove participant from team' }
    }
  )

  getCompetitionParticipations = withApiErrorHandling(
    async (competitionId: number, status: string | null = null): Promise<Participation[]> => {
      const url = status
        ? `/competitions/${competitionId}/participations?status=${status}`
        : `/competitions/${competitionId}/participations`
      const response = await apiClient.get<Participation[]>(url, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to fetch participations for competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load participations' }
    }
  )

  updateParticipation = withApiErrorHandling(
    async (participationId: number, updateData: UpdateParticipationRequest): Promise<Participation> => {
      const response = await apiClient.put<Participation>(`/participations/${participationId}`, updateData)
      return response.data
    },
    {
      logContext: 'Failed to update participation',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update participation' }
    }
  )

  confirmParticipation = withApiErrorHandling(
    async (participationId: number, sendEmail?: boolean): Promise<Participation> => {
      const response = await apiClient.put<Participation>(
        `/participations/${participationId}/confirm`,
        sendEmail !== undefined ? { sendEmail } : undefined
      )
      return response.data
    },
    {
      logContext: 'Failed to confirm participation',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to confirm participation' }
    }
  )

  moveToWaitlist = withApiErrorHandling(
    async (participationId: number): Promise<Participation> => {
      const response = await apiClient.put<Participation>(`/participations/${participationId}/waitlist`)
      return response.data
    },
    {
      logContext: 'Failed to move participation to waitlist',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to move to waitlist' }
    }
  )

  markAsPaid = withApiErrorHandling(
    async (participationId: number, participantFeeIds: Record<number, number>): Promise<Participation> => {
      const response = await apiClient.put<Participation>(
        `/participations/${participationId}/pay`,
        { participantFeeIds }
      )
      return response.data
    },
    {
      logContext: 'Failed to mark participation as paid',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to mark as paid' }
    }
  )

  clearPayment = withApiErrorHandling(
    async (participationId: number, participantIds?: number[]): Promise<Participation> => {
      const query = participantIds && participantIds.length > 0
        ? `?participantIds=${participantIds.join(',')}`
        : ''
      const response = await apiClient.delete<Participation>(
        `/participations/${participationId}/pay${query}`
      )
      return response.data
    },
    {
      logContext: 'Failed to clear payment for participation',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to clear payment' }
    }
  )

  createBatchParticipations = withApiErrorHandling(
    async (competitionId: number, items: BatchParticipationItem[], forceConfirmed?: boolean, sendEmail?: boolean): Promise<Participation[]> => {
      const response = await apiClient.post<Participation[]>(
        `/competitions/${competitionId}/participation/batch`,
        { items, forceConfirmed, sendEmail }
      )
      return response.data
    },
    {
      logContext: 'Failed to create batch participations',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to register players' }
    }
  )

  // =============== Competition Rounds Methods ===============

  getCompetitionRounds = withApiErrorHandling(
    async (competitionId: number): Promise<CompetitionRound[]> => {
      const response = await apiClient.get<CompetitionRound[]>(`/competitions/${competitionId}/rounds`, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to fetch rounds for competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load rounds' }
    }
  )

  createRound = withApiErrorHandling(
    async (competitionId: number, roundData: Record<string, unknown>): Promise<CompetitionRound> => {
      const response = await apiClient.post<CompetitionRound>(`/competitions/${competitionId}/rounds`, roundData, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to create round for competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to create round' }
    }
  )

  updateRound = withApiErrorHandling(
    async (competitionId: number, roundId: number, updateData: Record<string, unknown>): Promise<CompetitionRound> => {
      const response = await apiClient.put<CompetitionRound>(`/competitions/${competitionId}/rounds/${roundId}`, updateData, { suppressErrorToast: true } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to update round',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update round' }
    }
  )

  deleteRound = withApiErrorHandling(
    async (competitionId: number, roundId: number): Promise<{ success: boolean }> => {
      await apiClient.delete(`/competitions/${competitionId}/rounds/${roundId}`, { suppressErrorToast: true } as Record<string, unknown>)
      return { success: true }
    },
    {
      logContext: 'Failed to delete round',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete round' }
    }
  )

  toggleFlightPublication = withApiErrorHandling(
    async (competitionId: number, roundId: number, publish: boolean, notifyParticipants: boolean, startTimeVisibility?: VisibilityAudience): Promise<unknown> => {
      const body: Record<string, unknown> = { publish, notifyParticipants: publish ? notifyParticipants : false }
      if (publish && startTimeVisibility !== undefined) {
        body.startTimeVisibility = startTimeVisibility
      }
      const response = await apiClient.put(
        `/competitions/${competitionId}/rounds/${roundId}/flights/publish`,
        body
      )
      return response.data
    },
    {
      logContext: 'Failed to toggle flight publication for round',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to toggle flight publication' }
    }
  )

  toggleBracketPublication = withApiErrorHandling(
    async (competitionId: number, seriesId: number, publish: boolean): Promise<unknown> => {
      const response = await apiClient.put(
        `/competitions/${competitionId}/series/${seriesId}/match-play/bracket/publish?publish=${publish}`
      )
      return response.data
    },
    {
      logContext: 'Failed to toggle bracket publication',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to toggle bracket publication' }
    }
  )

  toggleResultsPublication = withApiErrorHandling(
    async (competitionId: number, roundId: number, publish: boolean, sendNotifications?: boolean, resultsVisibility?: VisibilityAudience): Promise<unknown> => {
      const body: Record<string, unknown> = { publish, sendNotifications: sendNotifications ?? false }
      if (publish && resultsVisibility !== undefined) {
        body.resultsVisibility = resultsVisibility
      }
      const response = await apiClient.put(
        `/competitions/${competitionId}/rounds/${roundId}/results/publish`,
        body
      )
      return response.data
    },
    {
      logContext: 'Failed to toggle results publication for round',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to toggle results publication' }
    }
  )

  sendStartTimeNotifications = withApiErrorHandling(
    async (
      competitionId: number,
      roundId: number,
      participationIds: number[],
      participantIds?: number[]
    ): Promise<void> => {
      await apiClient.post(
        `/competitions/${competitionId}/rounds/${roundId}/start-time-notifications`,
        { participationIds, participantIds }
      )
    },
    {
      logContext: 'Failed to send start time notifications',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to send start time notifications' }
    }
  )

  // =============== Flight Methods ===============

  createFlight = withApiErrorHandling(
    async (roundCourseConfigId: number, noOfFlights: number): Promise<Flight[]> => {
      const response = await apiClient.post<Flight[]>('/flights', {
        roundCourseConfigId,
        noOfFlights
      })
      return response.data
    },
    {
      logContext: 'Failed to create flights',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to create flights' }
    }
  )

  getFlightsByRoundCourseConfigId = withApiErrorHandling(
    async (roundCourseConfigId: number, signal?: AbortSignal): Promise<Flight[]> => {
      const response = await apiClient.get<Flight[]>(`/flights/${roundCourseConfigId}`, { signal })
      return response.data
    },
    {
      logContext: 'Failed to fetch flights for round course config',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load flights' },
      allowCancel: true
    }
  )

  deleteFlights = withApiErrorHandling(
    async (flightIds: number[]): Promise<unknown> => {
      const response = await apiClient.delete('/flights', {
        data: { flightIds },
        suppressErrorToast: true
      } as Record<string, unknown>)
      return response.data
    },
    {
      logContext: 'Failed to delete flights',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete flights' }
    }
  )

  runFlightLottery = withApiErrorHandling(
    async (
      roundCourseConfigId: number,
      options?: {
        mode: 'RANDOM' | 'REGISTRATION_ORDER' | 'RESULTS_REVERSED'
        preserveExistingOrder: boolean
        respectStartTimePreferences: boolean
        lotteryBySeries: boolean
      }
    ): Promise<Flight[]> => {
      const response = await apiClient.post<Flight[]>(
        `/flights/${roundCourseConfigId}/lottery`,
        options || {}
      )
      return response.data
    },
    {
      logContext: 'Failed to run flight lottery',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to run lottery' }
    }
  )

  getAvailableParticipationsForFlight = withApiErrorHandling(
    async (flightId: number, entryId: number | null = null): Promise<unknown> => {
      const url = entryId
        ? `/flights/${flightId}/participations/available?entryId=${entryId}`
        : `/flights/${flightId}/participations/available`
      const response = await apiClient.get(url)
      return response.data
    },
    {
      logContext: 'Failed to fetch available participations for flight',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load available participations' }
    }
  )

  updateFlightEntry = withApiErrorHandling(
    async (flightId: number, entryId: number, participationId: number): Promise<unknown> => {
      const response = await apiClient.put(`/flights/${flightId}/entry/${entryId}`, {
        participationId
      })
      return response.data
    },
    {
      logContext: 'Failed to update flight entry',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update flight entry' }
    }
  )

  addFlightEntry = withApiErrorHandling(
    async (flightId: number, participationId: number): Promise<unknown> => {
      const response = await apiClient.post(`/flights/${flightId}/entry`, {
        participationId
      })
      return response.data
    },
    {
      logContext: 'Failed to add flight entry to flight',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to add flight entry' }
    }
  )

  reorderFlight = withApiErrorHandling(
    async (params: ReorderFlightParams): Promise<unknown> => {
      const response = await apiClient.put('/flights/reorder', {
        roundCourseConfigId: params.roundCourseConfigId,
        sourceFlightId: params.sourceFlightId,
        targetFlightId: params.targetFlightId,
        position: params.position
      })
      return response.data
    },
    {
      logContext: 'Failed to reorder flight',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to reorder flight' }
    }
  )

  updateFlight = withApiErrorHandling(
    async (flightId: number, startTime: string, holeNumber: number): Promise<Flight> => {
      const response = await apiClient.put<Flight>(`/flights/${flightId}`, {
        startTime,
        holeNumber
      })
      return response.data
    },
    {
      logContext: 'Failed to update flight',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update flight' }
    }
  )

  deleteFlightEntry = withApiErrorHandling(
    async (flightId: number, entryId: number): Promise<void> => {
      await apiClient.delete(`/flights/${flightId}/entry/${entryId}`,
        { suppressErrorToast: true } as Record<string, unknown>)
    },
    {
      logContext: 'Failed to delete flight entry',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete flight entry' }
    }
  )

  // =============== Results Methods ===============

  getRoundResults = withApiErrorHandling(
    async (competitionId: number, roundCourseConfigId: number): Promise<CompetitionResult[]> => {
      const response = await apiClient.get<CompetitionResult[]>(`/competition/${competitionId}/results/${roundCourseConfigId}`)
      return response.data
    },
    {
      logContext: 'Failed to fetch results for round',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load results' }
    }
  )

  getResultById = withApiErrorHandling(
    async (resultId: number): Promise<CompetitionResult> => {
      const response = await apiClient.get<CompetitionResult>(`/competition/results/${resultId}`)
      return response.data
    },
    {
      logContext: 'Failed to fetch result',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load result' }
    }
  )

  createResult = withApiErrorHandling(
    async (
      competitionId: number,
      flightEntryId: number,
      participantId: number,
      scores: HoleScoreInput[],
      status: string | null = null,
      disqualificationReason: string | null = null,
      doesNotAffectHandicap: boolean | null = null
    ): Promise<CompetitionResult> => {
      const payload: Record<string, unknown> = {
        flightEntryId,
        participantId,
        scores
      }
      if (doesNotAffectHandicap !== null) payload.doesNotAffectHandicap = doesNotAffectHandicap
      if (status) payload.status = status
      if (disqualificationReason) payload.disqualificationReason = disqualificationReason

      const response = await apiClient.post<CompetitionResult>(`/competition/${competitionId}/results`, payload)
      return response.data
    },
    {
      logContext: 'Failed to create result for competition',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to create result' }
    }
  )

  updateResult = withApiErrorHandling(
    async (
      resultId: number,
      scores: HoleScoreInput[],
      status: string | null = null,
      disqualificationReason: string | null = null,
      doesNotAffectHandicap: boolean | null = null,
      teeSetId: number | null = null,
      playedDate: string | null = null,
      playingHandicap: number | null = null
    ): Promise<CompetitionResult> => {
      const payload: Record<string, unknown> = { scores }
      if (doesNotAffectHandicap !== null) payload.doesNotAffectHandicap = doesNotAffectHandicap
      if (status) payload.status = status
      if (disqualificationReason) payload.disqualificationReason = disqualificationReason
      if (teeSetId !== null) payload.teeSetId = teeSetId
      if (playedDate !== null) payload.playedDate = playedDate
      if (playingHandicap !== null) payload.playingHandicap = playingHandicap

      const response = await apiClient.put<CompetitionResult>(`/competition/results/${resultId}`, payload)
      return response.data
    },
    {
      logContext: 'Failed to update result',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update result' }
    }
  )

  deleteResult = withApiErrorHandling(
    async (resultId: number): Promise<{ success: boolean }> => {
      await apiClient.delete(`/competition/results/${resultId}`)
      return { success: true }
    },
    {
      logContext: 'Failed to delete result',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete result' }
    }
  )

  // =============== Rankings Methods ===============

  getRankings = withApiErrorHandling(
    async (seriesId: number, roundIds: number[], resultType: string, cumulative: boolean): Promise<ScoringRankingResult[]> => {
      const queryParams = new URLSearchParams()
      queryParams.append('roundIds', String(roundIds))
      queryParams.append('resultType', resultType)
      queryParams.append('cumulative', String(cumulative))

      const response = await apiClient.get<ScoringRankingResult[]>(
        `/series/${seriesId}/rankings?${queryParams}`
      )
      return response.data
    },
    {
      logContext: 'Failed to fetch rankings for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load rankings' }
    }
  )

  getIndividualRankings = withApiErrorHandling(
    async (seriesId: number, roundIds: number[], resultType: string, cumulative: boolean): Promise<ScoringRankingResult[]> => {
      const queryParams = new URLSearchParams()
      queryParams.append('roundIds', String(roundIds))
      queryParams.append('resultType', resultType)
      queryParams.append('cumulative', String(cumulative))

      const response = await apiClient.get<ScoringRankingResult[]>(
        `/series/${seriesId}/individual-rankings?${queryParams}`
      )
      return response.data
    },
    {
      logContext: 'Failed to fetch individual rankings for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load individual rankings' }
    }
  )

  // =============== Cut Methods ===============

  applyCut = withApiErrorHandling(
    async (
      competitionId: number,
      seriesId: number,
      cutLine: number,
      roundId: number
    ): Promise<CutResultDTO> => {
      const response = await apiClient.post<CutResultDTO>(
        `/competitions/${competitionId}/series/${seriesId}/cut`,
        { cutLine, roundId }
      )
      return response.data
    },
    {
      logContext: 'Failed to apply cut for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to apply cut' }
    }
  )

  removeCut = withApiErrorHandling(
    async (competitionId: number, seriesId: number): Promise<RemoveCutResultDTO> => {
      const response = await apiClient.delete<RemoveCutResultDTO>(
        `/competitions/${competitionId}/series/${seriesId}/cut`
      )
      return response.data
    },
    {
      logContext: 'Failed to remove cut for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to remove cut' }
    }
  )

  // =============== Weekly Competition Methods ===============

  getWeeklyResults = withApiErrorHandling(
    async (competitionId: number, seriesId: number): Promise<WeeklyPlayerResult[]> => {
      const response = await apiClient.get<WeeklyPlayerResult[]>(
        `/competitions/${competitionId}/series/${seriesId}/weekly-results`
      )
      return response.data
    },
    {
      logContext: 'Failed to fetch weekly results for series',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load weekly results' }
    }
  )

  // =============== Competition Media Methods ===============

  uploadCompetitionImage = withApiErrorHandling(
    async (competitionId: number, file: File): Promise<{ imageUrl: string }> => {
      const formData = new FormData()
      formData.append('image', file)

      const response = await apiClient.post<{ imageUrl: string; message: string }>(
        `/competitions/${competitionId}/image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return { imageUrl: response.data.imageUrl }
    },
    {
      logContext: 'Failed to upload competition image',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to upload image' }
    }
  )

  deleteCompetitionImage = withApiErrorHandling(
    async (competitionId: number): Promise<void> => {
      await apiClient.delete(`/competitions/${competitionId}/image`)
    },
    {
      logContext: 'Failed to delete competition image',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete image' }
    }
  )

  uploadCompetitionInvitation = withApiErrorHandling(
    async (competitionId: number, file: File): Promise<{ invitationUrl: string; invitationOriginalFilename?: string }> => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post<{ invitationUrl: string; invitationOriginalFilename?: string; message: string }>(
        `/competitions/${competitionId}/invitation`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return { invitationUrl: response.data.invitationUrl, invitationOriginalFilename: response.data.invitationOriginalFilename }
    },
    {
      logContext: 'Failed to upload competition invitation',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to upload invitation' }
    }
  )

  deleteCompetitionInvitation = withApiErrorHandling(
    async (competitionId: number): Promise<void> => {
      await apiClient.delete(`/competitions/${competitionId}/invitation`)
    },
    {
      logContext: 'Failed to delete competition invitation',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to delete invitation' }
    }
  )

  // =============== Methods without error handling (delegated to apiClient) ===============

  async getEligibleWeeklyCompetitions(roundDate?: string, scorecardId?: number): Promise<WeeklyCompetitionEligibility[]> {
    const params = new URLSearchParams()
    if (roundDate) params.append('roundDate', roundDate)
    if (scorecardId) params.append('scorecardId', scorecardId.toString())
    const queryString = params.toString() ? `?${params}` : ''
    const response = await apiClient.get<WeeklyCompetitionEligibility[]>(`/competitions/weekly/eligible${queryString}`)
    return response.data
  }

  async submitWeeklyCompetitionScore(resultId: number, competitionIds: number[]): Promise<void> {
    await apiClient.post('/competitions/weekly/submit', {
      resultId,
      competitionIds
    })
  }

  async createAdminScoreEntry(request: {
    participantId: number
    scorecardId: number
    teeSetId: number
    playedDate: string
    holeScores: HoleScoreInput[]
  }): Promise<void> {
    await apiClient.post('/competitions/weekly/admin/score-entry', request)
  }

  async getParticipantScores(participantId: number): Promise<WeeklyScoreDetail[]> {
    const response = await apiClient.get<WeeklyScoreDetail[]>(
      `/competitions/weekly/participant/${participantId}/scores`
    )
    return response.data || []
  }

  async deleteWeeklyScore(resultId: number): Promise<void> {
    await apiClient.delete(`/competitions/weekly/scores/${resultId}`)
  }

  // =============== Flight Collaborative Scoring Methods ===============

  submitFlightScore = withApiErrorHandling(
    async (flightId: number, scoreEntry: FlightScoreEntryRequest): Promise<HoleScoreDTO> => {
      const response = await apiClient.post<HoleScoreDTO>(`/flights/${flightId}/scores`, scoreEntry)
      return response.data
    },
    {
      logContext: 'Failed to submit flight score',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to submit score' }
    }
  )

  getFlightScores = withApiErrorHandling(
    async (flightId: number): Promise<FlightScoresDTO> => {
      const response = await apiClient.get<FlightScoresDTO>(`/flights/${flightId}/scores`)
      return response.data
    },
    {
      logContext: 'Failed to fetch flight scores',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load flight scores' }
    }
  )

  submitFlightScoresBatch = withApiErrorHandling(
    async (flightId: number, request: BatchScoreSubmissionRequest): Promise<BatchScoreSubmissionResponse> => {
      const response = await apiClient.post<BatchScoreSubmissionResponse>(`/flights/${flightId}/scores/batch`, request)
      return response.data
    },
    {
      logContext: 'Failed to submit batch flight scores',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to submit scores' }
    }
  )

  signFlightResults = withApiErrorHandling(
    async (flightId: number): Promise<void> => {
      await apiClient.post(`/flights/${flightId}/scores/sign`)
    },
    {
      logContext: 'Failed to sign flight results',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to sign results' }
    }
  )

  signResult = withApiErrorHandling(
    async (flightId: number, resultId: number): Promise<void> => {
      await apiClient.post(`/flights/${flightId}/results/${resultId}/sign`)
    },
    {
      logContext: 'Failed to sign result',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to sign result' }
    }
  )

  unsignResult = withApiErrorHandling(
    async (flightId: number, resultId: number): Promise<void> => {
      await apiClient.delete(`/flights/${flightId}/results/${resultId}/sign`)
    },
    {
      logContext: 'Failed to unsign result',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to unsign result' }
    }
  )

  // =============== eBirdie Submission Methods ===============

  submitToEBirdie = withApiErrorHandling(
    async (competitionId: number, roundId: number): Promise<EBirdieSubmitResponse> => {
      const response = await apiClient.post<EBirdieSubmitResponse>(
        `/competition/${competitionId}/rounds/${roundId}/ebirdie-submit`
      )
      return response.data
    },
    {
      logContext: 'Failed to submit to eBirdie',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to submit to eBirdie' }
    }
  )

  cancelEBirdie = withApiErrorHandling(
    async (competitionId: number, roundId: number): Promise<EBirdieCancelResponse> => {
      const response = await apiClient.post<EBirdieCancelResponse>(
        `/competition/${competitionId}/rounds/${roundId}/ebirdie-cancel`
      )
      return response.data
    },
    {
      logContext: 'Failed to cancel eBirdie submission',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to cancel eBirdie submission' }
    }
  )

  submitSelectedToEBirdie = withApiErrorHandling(
    async (competitionId: number, roundId: number, resultIds: number[]): Promise<EBirdieSubmitResponse> => {
      const response = await apiClient.post<EBirdieSubmitResponse>(
        `/competition/${competitionId}/rounds/${roundId}/ebirdie-submit-selected`,
        { resultIds }
      )
      return response.data
    },
    {
      logContext: 'Failed to submit selected results to eBirdie',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to submit selected results to eBirdie' }
    }
  )

  cancelSelectedEBirdie = withApiErrorHandling(
    async (competitionId: number, roundId: number, resultIds: number[]): Promise<EBirdieCancelResponse> => {
      const response = await apiClient.post<EBirdieCancelResponse>(
        `/competition/${competitionId}/rounds/${roundId}/ebirdie-cancel-selected`,
        { resultIds }
      )
      return response.data
    },
    {
      logContext: 'Failed to cancel selected eBirdie submissions',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to cancel selected eBirdie submissions' }
    }
  )

  addParticipantToTeam = withApiErrorHandling(
    async (competitionId: number, participationId: number, participantData: Record<string, unknown>): Promise<Participation> => {
      const response = await apiClient.post<Participation>(
        `/competitions/${competitionId}/participations/${participationId}/participants`,
        participantData
      )
      return response.data
    },
    {
      logContext: 'Failed to add participant to team',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to add participant to team' }
    }
  )

  sendParticipantEmail = withApiErrorHandling(
    async (competitionId: number, participationIds: number[], subject: string, body: string): Promise<void> => {
      await apiClient.post(
        `/competitions/${competitionId}/participants/email`,
        { participationIds, subject, body },
        { suppressErrorToast: true } as Record<string, unknown>
      )
    },
    {
      logContext: 'Failed to send participant email',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to send participant email' }
    }
  )

  refreshHandicaps = withApiErrorHandling(
    async (
      competitionId: number,
      participationIds: number[],
      updatePlayingHandicapInResults: boolean = false,
      updateResultsForParticipantIds?: number[]
    ): Promise<RefreshHandicapsResponse> => {
      const response = await apiClient.post<RefreshHandicapsResponse>(
        `/competitions/${competitionId}/participations/refresh-handicaps`,
        { participationIds, updatePlayingHandicapInResults, updateResultsForParticipantIds }
      )
      return response.data
    },
    {
      logContext: 'Failed to refresh participant handicaps',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to refresh handicaps' }
    }
  )

  // =============== Series Tee Override Methods ===============

  getSeriesTeeOverrides = withApiErrorHandling(
    async (seriesId: number): Promise<SeriesTeeOverridesResponse> => {
      const response = await apiClient.get<SeriesTeeOverridesResponse>(`/series/${seriesId}/tee-overrides`)
      return response.data
    },
    {
      logContext: 'Failed to fetch series tee overrides',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to load tee overrides' }
    }
  )

  updateSeriesTeeOverrides = withApiErrorHandling(
    async (seriesId: number, body: UpdateSeriesTeeOverridesRequest): Promise<UpdateSeriesTeeOverridesResponse> => {
      const response = await apiClient.put<UpdateSeriesTeeOverridesResponse>(`/series/${seriesId}/tee-overrides`, body)
      return response.data
    },
    {
      logContext: 'Failed to update series tee overrides',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to save tee overrides' }
    }
  )

  exportParticipantEmailsCsv = withApiErrorHandling(
    async (competitionId: number, participationIds: number[]): Promise<{ blob: Blob; filename: string }> => {
      const response = await apiClient.post<Blob>(
        `/competitions/${competitionId}/participants/email-export.csv`,
        { participationIds },
        { responseType: 'blob', suppressErrorToast: true } as Record<string, unknown>
      )
      const disposition = response.headers['content-disposition'] as string | undefined
      const match = disposition?.match(/filename="([^"]+)"/)
      const filename = match ? match[1] : `competition-${competitionId}-emails.csv`
      return { blob: response.data, filename }
    },
    {
      logContext: 'Failed to export participant emails',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to export participant emails' }
    }
  )

  updateVisibilityAudiences = withApiErrorHandling(
    async (
      competitionId: number,
      payload: { participantVisibility?: VisibilityAudience; startTimeVisibility?: VisibilityAudience; resultsVisibility?: VisibilityAudience }
    ): Promise<{ participantVisibility: VisibilityAudience; startTimeVisibility: VisibilityAudience; resultsVisibility: VisibilityAudience }> => {
      const response = await apiClient.patch<{ participantVisibility: VisibilityAudience; startTimeVisibility: VisibilityAudience; resultsVisibility: VisibilityAudience }>(
        `/competitions/${competitionId}/visibility-audiences`,
        payload
      )
      return response.data
    },
    {
      logContext: 'Failed to update visibility audiences',
      errorMessages: { ...competitionErrors, defaultMessage: 'Failed to update visibility audiences' }
    }
  )
}

export const competitionService = new CompetitionService()
